import React, { useState, useEffect } from 'react';

import { MultiSelect, MultiSelectProps } from '@mantine/core';
import { useDebouncedValue } from '@mantine/hooks';
import useAutoCompleteStaff from 'hooks/staff/useAutoCompleteStaff';
import { Staff } from 'types';

export interface IStaffMultiSelectAutoComplete extends Pick<Staff, 'staffId' | 'fullName'> {}

interface StaffMultiSelectAutoCompleteProps extends Omit<MultiSelectProps, 'data'> {
  value?: string[];
  isCoach?: boolean;
  onChange?: (values: string[]) => void;
  showNoStaff?: boolean;
}

const StaffMultiSelectAutoComplete = ({
  showNoStaff = true,
  ...props
}: StaffMultiSelectAutoCompleteProps): JSX.Element => {
  const { value, isCoach, onChange, ...msProps } = props;
  const [autocompleteData, setAutocompleteData] = useState<IStaffMultiSelectAutoComplete[]>([]);
  const [staffSearch, setStaffSearch] = useState<string>('');
  const [debouncedStaffSearchTerm] = useDebouncedValue<string>(staffSearch, 250);
  const { data, isSuccess } = useAutoCompleteStaff({
    search: debouncedStaffSearchTerm,
    isCoach,
  });
  const [selectedStaffs, setSelectedStaffs] = useState<IStaffMultiSelectAutoComplete[]>([]);

  const handleSearchChange = (search: string): void => {
    setStaffSearch(search);
  };

  useEffect(() => {
    if (isSuccess && data) {
      let results = data.data as Pick<Staff, 'staffId' | 'fullName'>[];
      if (showNoStaff) {
        results = [{ staffId: 'noStaff', fullName: isCoach ? 'No Coach' : 'No Staff' }].concat(results);
      }
      const updatedData = results.concat(
        selectedStaffs.filter(
          selectedStaff => !results.some(staff => staff.staffId === selectedStaff.staffId),
        ),
      );
      setAutocompleteData(updatedData);
    }
  }, [isSuccess, data, selectedStaffs, isCoach, showNoStaff]);

  const handleChange = (selectedItems: string[]): void => {
    if (onChange) {
      onChange(selectedItems);
    }

    const newSelectedStaffs = autocompleteData.filter(staff => selectedItems.includes(staff.staffId));
    setSelectedStaffs(newSelectedStaffs);
  };

  return (
    <MultiSelect
      {...msProps}
      value={value}
      onChange={handleChange}
      data={autocompleteData.map(staff => ({
        value: staff.staffId,
        label: staff.fullName,
      }))}
      clearable
      searchable
      placeholder={`Select ${isCoach ? 'coaches' : 'staff members'}`}
      nothingFound={`No ${isCoach ? 'coach' : 'staff member'} found`}
      onSearchChange={handleSearchChange}
    />
  );
};

export default StaffMultiSelectAutoComplete;
